import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

// Helper function to clean chapter titles (optional, but good practice)
const formatDisplayTitle = (title) => {
  if (!title) return '';
  const prefixRegex = /^(workshop|chapter|module)\s?\d+:\s*/i;
  return title.replace(prefixRegex, '').trim();
};

function Feedback({ setCurrentFeedback }) {
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token, user, authLoading } = useContext(AuthContext);
  const [feedback, setFeedback] = useState(null);
  const [chapterTitle, setChapterTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated === false) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Function to handle streaming feedback
  const streamFeedback = async (url, options = {}) => {
    setIsStreaming(true);
    setFeedback(''); // Clear existing feedback
    setError(null);

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to start streaming');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamedFeedback = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'chunk' && data.content) {
                streamedFeedback += data.content;
                setFeedback(streamedFeedback);
              } else if (data.type === 'complete') {
                // Update global feedback state
                if (setCurrentFeedback) {
                  const cleanFeedback = streamedFeedback.replace(/<[^>]*>/g, '') || '';
                  setCurrentFeedback(cleanFeedback);
                }
                setIsStreaming(false);
                return;
              } else if (data.type === 'error') {
                throw new Error(data.error || 'Streaming error');
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      setError(error.message || 'An error occurred during streaming');
      setIsStreaming(false);
    }
  };

  // Fetch feedback and chapter title when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      const fetchFeedbackAndTitle = async () => {
        setLoading(true);
        setError(null);
        
        try {
          // First, fetch chapter details
          const chapterRes = await fetch(`${import.meta.env.VITE_API_URL}/api/chapters/${chapterId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (chapterRes.ok) {
            const chapterData = await chapterRes.json();
            setChapterTitle(chapterData.title);
          } else {
            console.error('Error fetching chapter title:', await chapterRes.text());
            setChapterTitle('Unknown Chapter');
          }

          // Then try to get existing feedback first (non-streaming)
          const feedbackRes = await fetch(`${import.meta.env.VITE_API_URL}/api/feedback/${chapterId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (feedbackRes.ok) {
            const contentType = feedbackRes.headers.get('content-type');
            
            if (contentType && contentType.includes('text/event-stream')) {
              // Handle streaming response
              setLoading(false);
              await streamFeedback(`${import.meta.env.VITE_API_URL}/api/feedback/${chapterId}`);
            } else {
              // Handle regular JSON response (existing feedback)
              const feedbackData = await feedbackRes.json();
              setFeedback(feedbackData.feedback);
              
              if (setCurrentFeedback) {
                const cleanFeedback = feedbackData.feedback?.replace(/<[^>]*>/g, '') || '';
                setCurrentFeedback(cleanFeedback);
              }
              setLoading(false);
            }
          } else {
            const errorData = await feedbackRes.json();
            console.error('Error fetching feedback:', errorData.error);
            throw new Error(errorData.error || 'Failed to load feedback.');
          }

        } catch (error) {
          console.error('Error:', error);
          setError(error.message || 'An unexpected error occurred.');
          setLoading(false);
        }
      };

      fetchFeedbackAndTitle();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [chapterId, isAuthenticated, token, authLoading, setCurrentFeedback]);

  const handleRegenerate = async () => {
    try {
      setError(null);
      await streamFeedback(`${import.meta.env.VITE_API_URL}/api/feedback/${chapterId}/regenerate`, {
        method: 'POST',
      });
    } catch (err) {
      console.error('Error:', err);
      setError('An unexpected error occurred during regeneration.');
    }
  };

  // Only show spinner for auth loading or initial chapter title loading
  if (authLoading || (loading && !chapterTitle)) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error && !feedback) {
    return <div className="text-red-500 text-center mt-4">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto py-12 px-6">
        <h1 className="mb-8 text-2xl font-heading">
          Feedback: {formatDisplayTitle(chapterTitle) || 'Loading...'}
        </h1>
        {error && <div className="text-red-500 text-center mb-4">{error}</div>}
        <div className="font-body border border-gray-200 p-6 rounded-md">
          {feedback ? (
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: feedback }} />
          ) : isStreaming ? (
            <div className="flex items-center text-gray-500">
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-3"></div>
              Generating feedback...
            </div>
          ) : (
            <div className="flex items-center text-gray-500">
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-3"></div>
              Loading feedback...
            </div>
          )}

          {/* Show blinking cursor during streaming */}
          {isStreaming && feedback && (
            <span className="inline-block w-2 h-5 bg-black animate-pulse ml-1"></span>
          )}

          {user?.role === 'admin' && feedback && !isStreaming && (
            <button
              onClick={handleRegenerate}
              className="mt-12 bg-black text-white px-4 py-2 rounded transition flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isStreaming}
            >
              {isStreaming ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-3 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    ></path>
                  </svg>
                  Regenerating...
                </>
              ) : (
                'Regenerate Feedback'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Feedback;